import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type {
  AcademyDetails,
  AvailabilityResponse,
  Booking,
  ChangePasswordInput,
  CommunityFeed,
  CommunityPost,
  CommunityQuery,
  CommunityThread,
  CommunityThreadDetail,
  ContactMessageInput,
  CreateCommunityAnswerInput,
  CreateCommunityQuestionInput,
  CreateLessonBookingInput,
  CreateRoomBookingInput,
  Dashboard,
  FaqItem,
  LessonDetail,
  NotificationCategory,
  NotificationList,
  NotificationPreferences,
  PaymentDetail,
  PaymentsOverview,
  Room,
  RoomBooking,
  ScheduleQuery,
  ScheduleResponse,
  StudentProfile,
  Subject,
  TeacherDetail,
  TeacherQuery,
  TeacherSummary,
  Testimonial,
} from '@musicville/shared';
import { api, qs, request } from './api';

export const keys = {
  academy: () => ['academy'] as const,
  subjects: () => ['subjects'] as const,
  subject: (slug: string) => ['subjects', slug] as const,
  teachers: (query: Partial<TeacherQuery>) => ['teachers', query] as const,
  teacher: (slug: string) => ['teachers', slug] as const,
  relatedTeachers: (slug: string) => ['teachers', slug, 'related'] as const,
  availability: (params?: Record<string, unknown>) =>
    params ? (['availability', params] as const) : (['availability'] as const),
  rooms: (params?: Record<string, unknown>) => ['rooms', params ?? {}] as const,
  room: (slug: string) => ['rooms', slug] as const,
  roomAvailability: (slug: string, from?: string) => ['rooms', slug, 'availability', from] as const,
  faq: () => ['faq'] as const,
  testimonials: () => ['testimonials'] as const,
  community: () => ['community'] as const,
  booking: (reference: string) => ['bookings', reference] as const,
  myBookings: () => ['bookings', 'mine'] as const,

  portal: () => ['portal'] as const,
  dashboard: () => ['portal', 'dashboard'] as const,
  schedule: (params: Partial<ScheduleQuery>) => ['portal', 'schedule', params] as const,
  lesson: (reference: string) => ['portal', 'lessons', reference] as const,
  roomBooking: (reference: string) => ['portal', 'room-bookings', reference] as const,
  payments: () => ['portal', 'payments'] as const,
  payment: (invoiceNo: string) => ['portal', 'payments', invoiceNo] as const,
  notifications: (category?: NotificationCategory) =>
    ['portal', 'notifications', category ?? 'all'] as const,
  profile: () => ['portal', 'profile'] as const,
  threads: (query: Partial<CommunityQuery>) => ['community', 'threads', query] as const,
  thread: (slug: string) => ['community', 'threads', slug] as const,
};

export function useAcademyDetails() {
  return useQuery({
    queryKey: keys.academy(),
    queryFn: () => api.get<AcademyDetails>('/academy'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useSubjects() {
  return useQuery({
    queryKey: keys.subjects(),
    queryFn: () => api.get<Subject[]>('/subjects'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useSubject(slug: string | undefined) {
  return useQuery({
    queryKey: keys.subject(slug ?? ''),
    queryFn: () => api.get<Subject>(`/subjects/${slug}`),
    enabled: Boolean(slug),
    staleTime: 60 * 60 * 1000,
  });
}

export interface TeacherListResult {
  items: TeacherSummary[];
  total: number;
  page: number;
  perPage: number;
}

export function useTeachers(query: Partial<TeacherQuery> = {}) {
  return useQuery({
    queryKey: keys.teachers(query),
    queryFn: () => api.get<TeacherListResult>(`/teachers${qs(query as never)}`),
    staleTime: 60 * 1000,
    placeholderData: (previous) => previous,
  });
}

export function useTeacher(slug: string | undefined) {
  return useQuery({
    queryKey: keys.teacher(slug ?? ''),
    queryFn: () => api.get<TeacherDetail>(`/teachers/${slug}`),
    enabled: Boolean(slug),
  });
}

export function useRelatedTeachers(slug: string | undefined) {
  return useQuery({
    queryKey: keys.relatedTeachers(slug ?? ''),
    queryFn: () => api.get<TeacherSummary[]>(`/teachers/${slug}/related`),
    enabled: Boolean(slug),
  });
}

export interface AvailabilityParams extends Record<string, unknown> {
  subject?: string | undefined;
  teacherId?: string | undefined;
  from?: string | undefined;
  days?: number | undefined;
  type?: 'TRIAL' | 'REGULAR' | undefined;
}

export function useAvailability(
  params: AvailabilityParams,
  options?: Partial<UseQueryOptions<AvailabilityResponse>>,
) {
  return useQuery({
    queryKey: keys.availability(params),
    queryFn: () => api.get<AvailabilityResponse>(`/availability${qs(params as never)}`),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useRooms(params: { equipment?: string; capacity?: number; date?: string } = {}) {
  return useQuery({
    queryKey: keys.rooms(params),
    queryFn: () => api.get<Room[]>(`/rooms${qs(params as never)}`),
    staleTime: 60 * 1000,
  });
}

export function useRoom(slug: string | undefined) {
  return useQuery({
    queryKey: keys.room(slug ?? ''),
    queryFn: () => api.get<Room>(`/rooms/${slug}`),
    enabled: Boolean(slug),
  });
}

export function useRoomAvailability(slug: string | undefined, from?: string, days = 7) {
  return useQuery({
    queryKey: keys.roomAvailability(slug ?? '', from),
    queryFn: () =>
      api.get<AvailabilityResponse>(`/rooms/${slug}/availability${qs({ from, days })}`),
    enabled: Boolean(slug),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useFaq(category?: string) {
  return useQuery({
    queryKey: [...keys.faq(), category],
    queryFn: () => api.get<FaqItem[]>(`/faq${qs({ category })}`),
    staleTime: 60 * 60 * 1000,
  });
}

export function useTestimonials() {
  return useQuery({
    queryKey: keys.testimonials(),
    queryFn: () => api.get<Testimonial[]>('/testimonials'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useCommunity() {
  return useQuery({
    queryKey: keys.community(),
    queryFn: () => api.get<CommunityPost[]>('/community'),
    staleTime: 30 * 60 * 1000,
  });
}

export function useSendContactMessage() {
  return useMutation({
    mutationFn: (input: ContactMessageInput) => api.post<{ ok: true }>('/contact', input),
  });
}

export function useCreateLessonBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLessonBookingInput) => api.post<Booking>('/bookings/lessons', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.availability() });
      void queryClient.invalidateQueries({ queryKey: ['teachers'] });
      void queryClient.invalidateQueries({ queryKey: keys.myBookings() });
    },
    retry: false,
  });
}

export function useBooking(reference: string | undefined) {
  return useQuery({
    queryKey: keys.booking(reference ?? ''),
    queryFn: () => api.get<Booking>(`/bookings/lessons/${reference}`),
    enabled: Boolean(reference),
  });
}

export function useMyBookings(enabled: boolean) {
  return useQuery({
    queryKey: keys.myBookings(),
    queryFn: () => api.get<Booking[]>('/bookings/lessons'),
    enabled,
  });
}

export function useCreateRoomBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateRoomBookingInput) => api.post<RoomBooking>('/rooms/bookings', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
    retry: false,
  });
}

export function useRoomBooking(reference: string | undefined) {
  return useQuery({
    queryKey: ['rooms', 'bookings', reference],
    queryFn: () => api.get<RoomBooking>(`/rooms/bookings/${reference}`),
    enabled: Boolean(reference),
  });
}

export function useDashboard(enabled = true) {
  return useQuery({
    queryKey: keys.dashboard(),
    queryFn: () => api.get<Dashboard>('/portal/dashboard'),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useSchedule(params: Partial<ScheduleQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: keys.schedule(params),
    queryFn: () => api.get<ScheduleResponse>(`/portal/schedule${qs(params as never)}`),
    enabled,
    staleTime: 60 * 1000,
    placeholderData: (previous) => previous,
  });
}

export function useLessonDetail(reference: string | undefined) {
  return useQuery({
    queryKey: keys.lesson(reference ?? ''),
    queryFn: () => api.get<LessonDetail>(`/portal/lessons/${reference}`),
    enabled: Boolean(reference),
  });
}

export function useRoomBookingDetail(reference: string | undefined) {
  return useQuery({
    queryKey: keys.roomBooking(reference ?? ''),
    queryFn: () => api.get<LessonDetail>(`/portal/room-bookings/${reference}`),
    enabled: Boolean(reference),
  });
}

export function usePayments(enabled = true) {
  return useQuery({
    queryKey: keys.payments(),
    queryFn: () => api.get<PaymentsOverview>('/payments'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePaymentDetail(invoiceNo: string | undefined) {
  return useQuery({
    queryKey: keys.payment(invoiceNo ?? ''),
    queryFn: () => api.get<PaymentDetail>(`/payments/${invoiceNo}`),
    enabled: Boolean(invoiceNo),
  });
}

export function useNotifications(category?: NotificationCategory, enabled = true) {
  return useQuery({
    queryKey: keys.notifications(category),
    queryFn: () => api.get<NotificationList>(`/notifications${qs({ category })}`),
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: (previous) => previous,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<NotificationList>(`/notifications/${id}/read`),
    onSuccess: (list) => {
      queryClient.setQueryData(keys.notifications(), list);
      void queryClient.invalidateQueries({ queryKey: ['portal', 'notifications'] });
      void queryClient.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<NotificationList>('/notifications/read-all'),
    onSuccess: (list) => {
      queryClient.setQueryData(keys.notifications(), list);
      void queryClient.invalidateQueries({ queryKey: ['portal', 'notifications'] });
      void queryClient.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });
}

export function useProfile(enabled = true) {
  return useQuery({
    queryKey: keys.profile(),
    queryFn: () => api.get<StudentProfile>('/profile'),
    enabled,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      request<StudentProfile>('/profile', { method: 'PATCH', body: input }),
    onSuccess: (profile) => {
      queryClient.setQueryData(keys.profile(), profile);
      void queryClient.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: NotificationPreferences) =>
      request<StudentProfile>('/profile/notifications', { method: 'PUT', body: preferences }),
    onMutate: async (preferences) => {
      await queryClient.cancelQueries({ queryKey: keys.profile() });
      const previous = queryClient.getQueryData<StudentProfile>(keys.profile());
      if (previous) {
        queryClient.setQueryData<StudentProfile>(keys.profile(), { ...previous, preferences });
      }
      return { previous };
    },
    onError: (_error, _preferences, context) => {
      if (context?.previous) queryClient.setQueryData(keys.profile(), context.previous);
    },
    onSuccess: (profile) => queryClient.setQueryData(keys.profile(), profile),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => api.post<{ ok: true }>('/profile/password', input),
    retry: false,
  });
}

export function useCommunityFeed(query: Partial<CommunityQuery> = {}) {
  return useQuery({
    queryKey: keys.threads(query),
    queryFn: () => api.get<CommunityFeed>(`/community/threads${qs(query as never)}`),
    staleTime: 60 * 1000,
    placeholderData: (previous) => previous,
  });
}

export function useCommunityThread(slug: string | undefined) {
  return useQuery({
    queryKey: keys.thread(slug ?? ''),
    queryFn: () => api.get<CommunityThreadDetail>(`/community/threads/${slug}`),
    enabled: Boolean(slug),
  });
}

export function useAskQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCommunityQuestionInput) =>
      api.post<CommunityThread>('/community/threads', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['community'] });
      void queryClient.invalidateQueries({ queryKey: keys.dashboard() });
    },
    retry: false,
  });
}

export function usePostAnswer(slug: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCommunityAnswerInput) =>
      api.post<CommunityThreadDetail>(`/community/threads/${slug}/answers`, input),
    onSuccess: (thread) => {
      if (slug) queryClient.setQueryData(keys.thread(slug), thread);
      void queryClient.invalidateQueries({ queryKey: ['community', 'threads'] });
    },
    retry: false,
  });
}

interface HelpfulResult {
  helpfulCount: number;
  viewerFoundHelpful: boolean;
}

export function useToggleHelpful(slug: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (target: { answerId?: string }) =>
      api.post<HelpfulResult>(
        target.answerId
          ? `/community/answers/${target.answerId}/helpful`
          : `/community/threads/${slug}/helpful`,
      ),
    onSuccess: (result, target) => {
      if (!slug) return;

      queryClient.setQueryData<CommunityThreadDetail>(keys.thread(slug), (thread) => {
        if (!thread) return thread;
        if (!target.answerId) {
          return { ...thread, ...result };
        }
        return {
          ...thread,
          answers: thread.answers.map((answer) =>
            answer.id === target.answerId ? { ...answer, ...result } : answer,
          ),
        };
      });

      if (!target.answerId) {
        void queryClient.invalidateQueries({ queryKey: ['community', 'threads'] });
      }
    },
  });
}

export function useToggleSaved(slug: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<{ viewerSaved: boolean }>(`/community/threads/${slug}/saved`),
    onSuccess: (result) => {
      if (!slug) return;
      queryClient.setQueryData<CommunityThreadDetail>(keys.thread(slug), (thread) =>
        thread ? { ...thread, viewerSaved: result.viewerSaved } : thread,
      );
      void queryClient.invalidateQueries({ queryKey: ['community', 'threads'] });
    },
  });
}
